import dynamicfunction from '../../utils/dynamicfunction';
import Text from '../Text';
import { fontSizes } from '../../constants/constants';
import ViewReverse from '../ViewReverse';
import { cn } from '../../utils/utils';
import ErrorText from '../ErrorText';
import { useSelector } from 'react-redux';
import { getRejectedFieldData, getSectionRejectedFields, isRequiredEmpty } from '../../utils/functions';
import { ReactSVG } from 'react-svg';
import icons from '../../constants/icons';

const TextArea = ({
  value,
  onChange,
  placeholder,
  className,
  label,
  labelColor = 'text-primary-light',
  divClasses,
  isRequired = false,
  isDisabled = false,
  maxLength,
  onPressEnter,
  canEnterSpace = true,
  cols,
  props,
  error,
  labelDivClass,
  isLabelHeight = false,
  remarksMetadata = null,
  isArabicAllowed = false,
}) => {
  const { TextWeight, TextAlign } = dynamicfunction();
  const { rejection_summary, is_rejected, field_codes } = useSelector((state) => state.corporate);

  const arabicRegix = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\u0660-\u0669]/g;

  const handleChange = (e) => {
    const value = e?.target?.value || '';
    const clenedText = value?.replace(arabicRegix, '');
    if (isArabicAllowed) {
      onChange(value);
      return;
    }

    onChange(clenedText);
  };

  const rejectedFields = getSectionRejectedFields(rejection_summary, remarksMetadata?.sectionId);
  const rejectedField = rejectedFields?.length < 1 ? null : getRejectedFieldData(rejectedFields, field_codes, remarksMetadata?.key);

  const isFieldDisabled = isDisabled || (is_rejected && remarksMetadata && (!rejectedField?.remarks || rejectedField?.remarks?.length < 1));

  return (
    <div className={cn('w-full', divClasses)}>
      {
        label && (
          <div data-required-empty={isRequiredEmpty(isRequired, label, value)} className={cn(isLabelHeight ? 'md:h-9' : "h-6", labelDivClass)} >
            <Text
              text={label}
              size={fontSizes.h14}
              color={labelColor}
              className={cn('leading-4', isLabelHeight && 'mb-1')}
              isRequired={isRequired}
              isTruncate={false}
            />
          </div>
        )
      }
      <ViewReverse
        className={cn(
          'px-3 py-2 border border-grey rounded w-full items-center',
          className,
          isFieldDisabled && 'cursor-not-allowed bg-disabled',
          rejectedField?.remarks?.length > 0 && 'border-red-600',
        )}
      >
        <textarea
          value={value}
          onChange={handleChange}
          disabled={isFieldDisabled}
          cols={cols ?? 5}
          placeholder={placeholder}
          className={cn(
            TextWeight.regular,
            TextAlign,
            'outline-none w-full text-sm bg-transparent',
            isFieldDisabled ? 'text-dark-grey/50 cursor-not-allowed' : 'text-black',
            rejectedField?.remarks?.length > 0 && 'text-red-600',
          )}
          maxLength={maxLength}
          onKeyDown={(e) => {
            if (e.key === ' ' && !canEnterSpace) {
              e.preventDefault();
              return;
            }
            e.key === 'Enter' && onPressEnter && onPressEnter();
          }}
          onInput={(e) => {
            if (!canEnterSpace) {
              const value = e.target.value;
              if (value.endsWith('. ')) {
                e.target.value = value.slice(0, -2);
              }
            }
          }}
          {...props}
        />
      </ViewReverse>
      {error?.length > 0 && <ErrorText text={error} />}
      {
        rejectedField?.remarks?.length > 0 && (
          <ViewReverse className='items-center gap-2 mt-2'>
            <ReactSVG src={icons.field_info_outline} />
            <ErrorText text={rejectedField?.remarks} className={'mt-0'} />
          </ViewReverse>
        )
      }
    </div>
  );
};

export default TextArea;
